import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { ReadableContent, LeadParagraph, EnhancedList, Callout } from '../../../components/ui/ContentFormatter';

const CompanyOverview = () => {
  const values = [
    {
      icon: 'Target',
      title: 'Strategic Excellence',
      description: 'Delivering transformative GCC strategies that align with global enterprise objectives and local market dynamics.'
    },
    {
      icon: 'Lightbulb',
      title: 'Innovation Leadership',
      description: 'Pioneering cutting-edge methodologies in GCC establishment, talent acquisition, and operational optimization.'
    },
    {
      icon: 'Shield',
      title: 'Trust & Integrity',
      description: 'Building enduring partnerships through transparent communication, ethical practices, and unwavering commitment.'
    },
    {
      icon: 'Users',
      title: 'Collaborative Partnership',
      description: 'Working as an extension of our clients\' teams to ensure seamless integration and sustainable growth.'
    }
  ];

  const capabilities = [
    {
      category: 'GCC Strategy & Setup',
      services: ['Market Entry Strategy', 'Location Analysis', 'Regulatory Compliance', 'Infrastructure Planning']
    },
    {
      category: 'Talent Acquisition',
      services: ['Executive Search', 'Technical Recruitment', 'Cultural Integration', 'Retention Strategies']
    },
    {
      category: 'Operational Excellence',
      services: ['Process Optimization', 'Quality Assurance', 'Performance Metrics', 'Continuous Improvement']
    },
    {
      category: 'Advisory Services',
      services: ['Strategic Consulting', 'Market Intelligence', 'Risk Management', 'Scaling Strategies']
    }
  ];

  return (
    <section className="py-16" id="overview">
      <div className="container-executive">
        {/* Company Mission */}
        <div className="max-w-4xl mx-auto text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-heading-xl font-bold text-text-primary mb-6">
              Rebuilt to Launch Service-First GCCs
            </h2>
            <p className="text-lead text-secondary mb-8">
              Bootminds was founded in 2017 to help global enterprises tap India’s engineering potential. After partnering with
              Intelliswift in 2018 and guiding large-scale GCC programs through that alliance, we exited when Intelliswift was
              acquired by LTTS in late 2024. In January 2025 we reinitiated Bootminds as an independent firm laser-focused on
              transparent GCC setup, expansion, executive hiring, and SkillStack™ staffing.
            </p>
            <div className="bg-gradient-to-r from-primary/10 to-accent/10 p-6 rounded-lg border border-primary/20">
              <p className="text-body-lg font-medium text-text-primary">
                "We bring the lessons from our 2018-2024 Intelliswift run into a rebooted, service-first Bootminds—grounded in
                transparent commercials, zero-risk engagement models, and SkillStack™ intelligence that shortens GCC launch
                timelines."
              </p>
            </div>
          </motion.div>
        </div>

        {/* Core Values */}
        <div className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-center mb-12"
          >
            <h3 className="text-heading-lg font-bold text-text-primary mb-4">
              Our Core Values
            </h3>
            <p className="text-body-md text-secondary max-w-2xl mx-auto">
              The principles that guide every decision, every partnership, and every strategic initiative we undertake.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 + (index * 0.1) }}
                className="bg-background border border-border rounded-lg p-6 hover:border-primary/30 hover:shadow-lg transition-all duration-300"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name={value.icon} size={24} className="text-primary" />
                </div>
                <h4 className="text-heading-sm font-semibold text-text-primary mb-3">
                  {value.title}
                </h4>
                <p className="text-body-sm text-secondary">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Company Story */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <h3 className="text-2xl lg:text-3xl font-heading font-heading-bold text-text-primary mb-6">
              Our Founding Story
            </h3>
            <ReadableContent>
              <LeadParagraph>
                Bootminds began in 2017 when our founding team left global operators to build a boutique GCC advisory
                in Bangalore. We partnered with Intelliswift in 2018 to blend strategy with large-scale delivery and ran
                end-to-end GCC launches under that banner until Intelliswift’s acquisition by LTTS in late 2024.
              </LeadParagraph>
              
              <p>
                During that period we proved the need for transparent pricing, government partnership clarity, and
                leadership hiring that can stand up to board scrutiny. The exit created the space to design Bootminds 2.0—an
                independent, service-first partner that aligns commercial models with outcomes.
              </p>
              
              <Callout variant="info" title="Our Solution" icon="Lightbulb">
                Today we operate four integrated service pillars—GCC Setup, GCC Expansion, Executive Hiring, and SkillStack™
                Staffing—each governed by transparent pricing, 25-day talent SLAs, and zero-risk disengagement clauses.
              </Callout>
            </ReadableContent>
            <div className="mt-6">
              <Button
                variant="outline"
                iconName="Calendar"
                iconPosition="left"
                asChild
              >
                <a href="/executive-contact-strategic-engagement">
                  Schedule Origin Briefing
                </a>
              </Button>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="relative"
          >
            <div className="bg-gradient-to-br from-primary/20 to-accent/20 rounded-lg p-8 border border-primary/20">
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-heading font-heading-bold text-text-primary mb-2">2017</div>
                  <div className="text-sm text-text-secondary">Bootminds Founded</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-heading font-heading-bold text-text-primary mb-2">2018-24</div>
                  <div className="text-sm text-text-secondary">Intelliswift Alliance</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-heading font-heading-bold text-text-primary mb-2">2025</div>
                  <div className="text-sm text-text-secondary">Independent Relaunch</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-heading font-heading-bold text-text-primary mb-2">4</div>
                  <div className="text-sm text-text-secondary">Integrated Service Pillars</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Capabilities Matrix */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mb-16"
        >
          <div className="text-center mb-12">
            <h3 className="text-2xl lg:text-3xl font-heading font-heading-bold text-text-primary mb-4">
              Comprehensive Capabilities
            </h3>
            <p className="text-text-secondary max-w-3xl mx-auto">
              Our expertise spans the entire GCC lifecycle, from strategic planning to operational excellence, 
              ensuring sustainable success at every stage of your journey.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {capabilities.map((capability, index) => (
              <div
                key={index}
                className="bg-muted rounded-lg p-6 hover:bg-primary/5 transition-colors duration-300"
              >
                <h4 className="font-heading font-heading-semibold text-lg text-text-primary mb-4">
                  {capability.category}
                </h4>
                <ul className="space-y-2">
                  {capability.services.map((service, serviceIndex) => (
                    <li key={serviceIndex} className="flex items-center text-sm text-text-secondary">
                      <Icon name="CheckCircle" size={14} className="text-accent mr-2 flex-shrink-0" />
                      {service}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Market Position */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="bg-gradient-to-r from-primary/5 to-accent/5 rounded-lg p-8 lg:p-12"
        >
          <h3 className="text-2xl lg:text-3xl font-heading font-heading-bold text-text-primary mb-6 text-center">
            Why CXOs Re-engage Bootminds in 2025
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-background border border-border rounded-xl p-6 shadow-sm">
              <Icon name="Receipt" size={24} className="text-primary mb-3" />
              <h4 className="text-lg font-semibold text-text-primary mb-2">Transparent Commercials</h4>
              <p className="text-sm text-text-secondary">
                Open rate cards with pass-through government costs, success-based fees only when milestones land.
              </p>
            </div>
            <div className="bg-background border border-border rounded-xl p-6 shadow-sm">
              <Icon name="Shield" size={24} className="text-primary mb-3" />
              <h4 className="text-lg font-semibold text-text-primary mb-2">Zero-Risk Engagement</h4>
              <p className="text-sm text-text-secondary">
                3-6 month walk-away clause, steady-state handover playbooks, and governance guardrails audited quarterly.
              </p>
            </div>
            <div className="bg-background border border-border rounded-xl p-6 shadow-sm">
              <Icon name="Layers" size={24} className="text-primary mb-3" />
              <h4 className="text-lg font-semibold text-text-primary mb-2">SkillStack™ Staffing</h4>
              <p className="text-sm text-text-secondary">
                25-day talent promise with AI-backed assessments, candidate experience concierge, and retention analytics.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CompanyOverview;